<template>
  <div class="cit-modal-overlay" @click.self="$emit('close')">
    <div class="cit-modal-content">
      <button class="cit-close" @click="$emit('close')" title="Fermer">×</button>

      <div class="cit-header">
        <div class="cit-title-row">
          <h3 class="cit-title">{{ fileLabel || 'Source' }}</h3>
          <span v-if="pagesLabel" class="cit-pages-badge">{{ pagesLabel }}</span>
        </div>
        <div v-if="citation.title" class="cit-meta" :title="citation.title">
          {{ citation.title }}
        </div>
      </div>

      <div ref="scroll" class="cit-body">
        <div v-if="loading" class="cit-state">Chargement du document…</div>
        <div v-else-if="error" class="cit-state cit-state--error">
          {{ error }}
          <button class="cit-retry" @click="fetchAndRender">Réessayer</button>
        </div>
        <div v-else class="cit-pages">
          <div
            v-for="p in numPages"
            :key="p"
            :ref="`pageWrap-${p}`"
            class="cit-page-wrap"
            :class="{ 'cit-page-wrap--highlight': isHighlighted(p) }"
          >
            <span class="cit-page-num">Page {{ p }}<span v-if="isHighlighted(p)" class="cit-page-tag">cité</span></span>
            <canvas :ref="`canvas-${p}`" class="cit-canvas"></canvas>
          </div>
        </div>
      </div>

      <div class="cit-footer">
        <a v-if="signedUrl" :href="signedUrl" target="_blank" rel="noopener noreferrer" class="cit-open">
          Ouvrir le PDF dans un nouvel onglet ↗
        </a>
        <button class="cit-close-btn" @click="$emit('close')">Fermer</button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { getSupabaseToken } from '@/services/api';

// pdfjs-dist est chargé dynamiquement à l'ouverture du modal (cf. fetchAndRender) :
// ça sort la lib du chunk-vendors principal et allège le bundle déployé.

const API_URL = process.env.VUE_APP_API_URL || 'https://api.query-juriste.com';

let _pdfjsLibPromise = null;
function loadPdfjs() {
  if (!_pdfjsLibPromise) {
    _pdfjsLibPromise = import(/* webpackChunkName: "pdfjs" */ 'pdfjs-dist/legacy/build/pdf').then((mod) => {
      const lib = mod.default || mod;
      if (lib.GlobalWorkerOptions && !lib.GlobalWorkerOptions.workerSrc) {
        const v = lib.version || '3.11.174';
        lib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${v}/pdf.worker.min.js`;
      }
      return lib;
    });
  }
  return _pdfjsLibPromise;
}

export default {
  name: 'CitationPreviewModal',
  props: {
    citation: {
      type: Object,
      required: true,
      // { nodeId, workspaceId, title, page_start, page_end, source_file, scaleway_key }
    },
  },
  data() {
    return {
      loading: false,
      error: '',
      signedUrl: '',
      pdfBlobUrl: '',
      numPages: 0,
      pdfDoc: null,
      renderTasks: [],
    };
  },
  computed: {
    pagesLabel() {
      const s = this.citation.page_start;
      const e = this.citation.page_end;
      if (s == null) return '';
      if (e == null || e === s) return `Page ${s}`;
      return `Pages ${s}–${e}`;
    },
    fileLabel() {
      const src = this.citation.source_file || '';
      if (!src) return '';
      const idx = src.lastIndexOf('/');
      return idx >= 0 ? src.slice(idx + 1) : src;
    },
  },
  mounted() {
    this.fetchAndRender();
    window.addEventListener('keydown', this.onKeyDown);
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.onKeyDown);
    this.cancelAllRenders();
    if (this.pdfDoc && typeof this.pdfDoc.destroy === 'function') {
      try { this.pdfDoc.destroy(); } catch (e) { /* noop */ }
    }
    this.pdfDoc = null;
    if (this.pdfBlobUrl) {
      try { URL.revokeObjectURL(this.pdfBlobUrl); } catch (e) { /* noop */ }
      this.pdfBlobUrl = '';
    }
  },
  methods: {
    onKeyDown(e) {
      if (e.key === 'Escape') this.$emit('close');
    },
    isHighlighted(p) {
      const s = this.citation.page_start;
      const e = this.citation.page_end || s;
      if (s == null) return false;
      return p >= s && p <= e;
    },
    cancelAllRenders() {
      this.renderTasks.forEach((t) => { try { t.cancel(); } catch (e) { /* noop */ } });
      this.renderTasks = [];
    },
    async fetchAndRender() {
      if (!this.citation || !this.citation.scaleway_key || !this.citation.workspaceId) {
        this.error = 'Informations de citation incomplètes.';
        return;
      }
      this.loading = true;
      this.error = '';
      this.numPages = 0;
      this.signedUrl = '';
      if (this.pdfBlobUrl) {
        try { URL.revokeObjectURL(this.pdfBlobUrl); } catch (e) { /* noop */ }
        this.pdfBlobUrl = '';
      }
      this.cancelAllRenders();

      let token;
      try {
        token = await getSupabaseToken();
      } catch (e) {
        this.error = 'Session expirée, rafraîchis la page.';
        this.loading = false;
        return;
      }

      // 1) Récupère l'URL signée (pour "Ouvrir dans un nouvel onglet")
      //    — ne bloque pas le rendu si ça échoue.
      axios
        .post(
          `${API_URL}/copilot/api/v1/workspace/${this.citation.workspaceId}/signed-url`,
          { scaleway_key: this.citation.scaleway_key },
          { headers: { Authorization: `Bearer ${token}` } },
        )
        .then((r) => { this.signedUrl = r.data.url; })
        .catch((e) => { console.warn('signed-url fetch failed (non-blocking)', e); });

      // 2) Streame les bytes via le backend (même-origine → pas de CORS) et
      //    fabrique un blob URL pour pdf.js.
      let pdfResponse;
      try {
        pdfResponse = await axios.post(
          `${API_URL}/copilot/api/v1/workspace/${this.citation.workspaceId}/fetch-pdf`,
          { scaleway_key: this.citation.scaleway_key },
          {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob',
          },
        );
      } catch (e) {
        console.error('fetch-pdf proxy failed', e);
        this.error = 'Impossible de récupérer le document.';
        this.loading = false;
        return;
      }
      this.pdfBlobUrl = URL.createObjectURL(pdfResponse.data);

      try {
        const pdfjsLib = await loadPdfjs();
        const loadingTask = pdfjsLib.getDocument({ url: this.pdfBlobUrl });
        const pdf = await loadingTask.promise;
        this.pdfDoc = pdf;
        this.numPages = pdf.numPages;
        this.loading = false;
        // Wait for Vue to mount the page-wrap divs + canvases
        await this.$nextTick();
        await this.renderAllPages();
        // Auto-scroll to first highlighted page
        this.scrollToHighlighted();
      } catch (e) {
        console.error('pdf load/render failed', e);
        this.error = 'Impossible d\'afficher le PDF.';
        this.loading = false;
      }
    },
    async renderAllPages() {
      if (!this.pdfDoc) return;
      const containerWidth = this.$refs.scroll ? this.$refs.scroll.clientWidth - 32 : 800;
      // Rendre séquentiellement pour limiter la charge mémoire sur les gros PDFs.
      for (let p = 1; p <= this.numPages; p += 1) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await this.renderPage(p, containerWidth);
        } catch (e) {
          if (e && e.name === 'RenderingCancelledException') return;
          console.warn(`render page ${p} failed`, e);
        }
      }
    },
    async renderPage(pageNum, containerWidth) {
      const canvasRef = this.$refs[`canvas-${pageNum}`];
      const canvas = Array.isArray(canvasRef) ? canvasRef[0] : canvasRef;
      if (!canvas) return;
      const page = await this.pdfDoc.getPage(pageNum);
      const unscaled = page.getViewport({ scale: 1 });
      const scale = Math.min(containerWidth / unscaled.width, 2); // cap à x2
      const viewport = page.getViewport({ scale });
      const dpr = window.devicePixelRatio || 1;
      canvas.width = viewport.width * dpr;
      canvas.height = viewport.height * dpr;
      canvas.style.width = `${viewport.width}px`;
      canvas.style.height = `${viewport.height}px`;
      const ctx = canvas.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const renderTask = page.render({ canvasContext: ctx, viewport });
      this.renderTasks.push(renderTask);
      await renderTask.promise;
    },
    scrollToHighlighted() {
      const start = this.citation.page_start;
      if (!start) return;
      const wrapRef = this.$refs[`pageWrap-${start}`];
      const el = Array.isArray(wrapRef) ? wrapRef[0] : wrapRef;
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },
  },
};
</script>

<style scoped>
.cit-modal-overlay {
  position: fixed;
  top: 0; left: 0; width: 100%; height: 100%;
  background-color: rgba(0, 0, 0, 0.65);
  display: flex; justify-content: center; align-items: center;
  z-index: 1100;
}
.cit-modal-content {
  position: relative;
  background: #fff;
  border-radius: 10px;
  width: 1000px;
  max-width: 96vw;
  height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0,0,0,0.35);
}
.cit-close {
  position: absolute;
  top: 10px; right: 12px;
  background: transparent; border: none;
  font-size: 1.8rem; line-height: 1; color: #9ca3af;
  cursor: pointer; padding: 2px 6px;
  z-index: 2;
}
.cit-close:hover { color: #374151; }

.cit-header {
  padding: 16px 22px 12px;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}
.cit-title-row {
  display: flex; align-items: center; gap: 12px;
  margin-right: 30px;
}
.cit-title {
  margin: 0; font-size: 1.02rem; font-weight: 600;
  color: #111827;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.cit-pages-badge {
  background: #fef08a; color: #713f12;
  font-size: 0.75rem; font-weight: 600;
  padding: 3px 10px; border-radius: 10px;
  white-space: nowrap;
  flex-shrink: 0;
}
.cit-meta {
  margin-top: 6px;
  font-size: 0.82rem; color: #6b7280;
  line-height: 1.4;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

.cit-body {
  flex: 1;
  overflow-y: auto;
  background: #e5e7eb;
  padding: 16px;
  min-height: 0;
}
.cit-pages {
  display: flex; flex-direction: column; align-items: center;
  gap: 16px;
}
.cit-page-wrap {
  position: relative;
  background: #fff;
  box-shadow: 0 2px 6px rgba(0,0,0,0.15);
  transition: box-shadow 0.25s ease;
  max-width: 100%;
}
.cit-page-wrap--highlight {
  box-shadow: 0 0 0 3px #facc15, 0 4px 12px rgba(250, 204, 21, 0.4);
  background: #fffbe6;
}
.cit-page-wrap--highlight::after {
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(250, 204, 21, 0.18);
  pointer-events: none;
  mix-blend-mode: multiply;
}
.cit-page-num {
  position: absolute;
  top: -10px; left: 10px;
  background: #374151; color: #fff;
  font-size: 0.7rem; font-weight: 500;
  padding: 2px 8px; border-radius: 10px;
  z-index: 1;
  display: flex; align-items: center; gap: 6px;
}
.cit-page-wrap--highlight .cit-page-num {
  background: #ca8a04;
}
.cit-page-tag {
  background: #fde047; color: #713f12;
  padding: 0 5px; border-radius: 6px;
  font-size: 0.65rem; font-weight: 600;
  text-transform: uppercase;
}
.cit-canvas {
  display: block;
  max-width: 100%;
}

.cit-state {
  margin: auto; color: #6b7280; font-size: 0.9rem;
  text-align: center; padding: 40px;
}
.cit-state--error { color: #b91c1c; }
.cit-retry {
  display: block;
  margin: 12px auto 0;
  padding: 6px 14px;
  background: #fff; border: 1px solid #d1d5db;
  border-radius: 6px; font-size: 0.82rem; cursor: pointer;
}

.cit-footer {
  padding: 12px 22px;
  border-top: 1px solid #e5e7eb;
  display: flex; justify-content: space-between; align-items: center;
  gap: 10px;
  flex-shrink: 0;
}
.cit-open {
  color: #6d28d9; text-decoration: none;
  font-size: 0.85rem; font-weight: 500;
}
.cit-open:hover { text-decoration: underline; }
.cit-close-btn {
  background: #e5e7eb; color: #374151; border: none;
  padding: 8px 18px; border-radius: 6px; cursor: pointer; font-weight: 500;
}
.cit-close-btn:hover { background: #d1d5db; }
</style>
